import { useCallback, useEffect, useRef, useState } from "react";

// Checkbox selection over a list of row ids, with shift-click ranges.
// `orderedIds` is the current filtered+sorted order; ids that leave it are dropped.
export default function useRowSelection(orderedIds) {
  const [selected, setSelected] = useState(() => new Set());
  const anchorRef = useRef(null);

  useEffect(() => {
    setSelected((prev) => {
      if (prev.size === 0) return prev;
      const present = new Set(orderedIds);
      const next = new Set([...prev].filter((id) => present.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [orderedIds]);

  const toggle = useCallback(
    (id, { range = false } = {}) => {
      setSelected((prev) => {
        const next = new Set(prev);
        const anchor = anchorRef.current;
        if (range && anchor !== null && anchor !== id) {
          const a = orderedIds.indexOf(anchor);
          const b = orderedIds.indexOf(id);
          if (a !== -1 && b !== -1) {
            const shouldSelect = !prev.has(id);
            const [from, to] = a < b ? [a, b] : [b, a];
            for (let i = from; i <= to; i++) {
              if (shouldSelect) next.add(orderedIds[i]);
              else next.delete(orderedIds[i]);
            }
            anchorRef.current = id;
            return next;
          }
        }
        if (next.has(id)) next.delete(id);
        else next.add(id);
        anchorRef.current = id;
        return next;
      });
    },
    [orderedIds]
  );

  const setMany = useCallback((ids, value) => {
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (value ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    anchorRef.current = null;
    setSelected(new Set());
  }, []);

  return { selected, toggle, setMany, clear };
}
